import { Controller } from "@hotwired/stimulus";
import { post } from "@rails/request.js";

// Lifts Craig's draft into the textarea, where it belongs to the operator to edit.
export default class extends Controller {
  static targets = ["template", "result", "field", "regenerate", "preview", "draftExecutionId"];
  static values = {
    url: String,
    targetId: String,
    previewUrl: String,
    previewTargetId: String,
    previewError: String,
    phase: String,
    agentName: String,
    agentPlace: String,
    actionId: String,
  };

  connect() {
    // A JS property, so it survives a Turbo morph.
    this.history ||= [];
    this.revealIfWritten();
  }

  // Drafting is deterministic, so an unchanged condition would redraft what is already there.
  refreshRegenerate() {
    if (this.hasRegenerateTarget)
      this.regenerateTarget.classList.toggle("disabled", !this.redraftable());
  }

  redraftable() {
    if (this.drafting) return false;
    if (this.drafted === undefined) return true;

    return this.criteria() !== this.draftedFrom || this.templateTarget.value !== this.drafted;
  }

  // Adopting on arrival, not after the fetch: the stream applies a frame later.
  resultTargetConnected() {
    if (this.adoptDraft()) return;
    // Nothing more is coming for this one -- give the operator the box and the button back.
    if (this.drafting && this.resultTarget.dataset.drafting !== "true") return this.giveUp();

    // A draft arrives twice: the completion broadcast, and turbo-broadcast-sync's answer to
    // the question it asks as it connects. Whichever lands second brings a Regenerate link
    // rendered from the execution, which knows nothing about what is in the box -- so
    // whether there is anything new to draft from has to be answered again.
    this.refreshRegenerate();
  }

  // A message the operator already wrote is left alone.
  draftIfEmpty() {
    if (this.templateTarget.value.trim()) return this.reveal();

    this.request();
  }

  regenerate(event) {
    event.preventDefault();
    // Covers the gap before the server-rendered disabled state arrives.
    if (this.regenerateTarget.classList.contains("disabled")) return;

    this.request();
  }

  undo(event) {
    event.preventDefault();
    if (this.history.length === 0) return;

    this.write(this.history.pop(), { remember: false });
  }

  // Sends what is typed, not what is saved -- previewing an unsaved edit is the point.
  // Each click buys a discovery, a live fetch and a render, so an empty box is not worth
  // one and a second click is ignored while the first is still running.
  async preview(event) {
    event.preventDefault();
    if (this.previewing || !this.templateTarget.value.trim()) return;

    this.previewing = true;
    this.togglePreview(true);
    try {
      const response = await post(this.previewUrlValue, {
        body: {
          criteria: this.criteria(),
          place: this.agentPlace(),
          message_template: this.templateTarget.value,
          target: this.previewTargetIdValue,
          // What the message was authored against, so the preview replays the send's
          // selection instead of drawing its own.
          draft_execution_id: this.draftExecutionId(),
          action_id: this.actionIdValue,
        },
        responseKind: "turbo-stream",
      });

      // Without the slot the response should have rendered, nothing will ever announce a
      // preview -- and the operator is left clicking a link that appears to do nothing.
      if (!response.ok) this.previewFailed();
    } catch {
      this.previewFailed();
    } finally {
      this.previewing = false;
      this.togglePreview(false);
    }
  }

  draftExecutionId() {
    return this.hasDraftExecutionIdTarget ? this.draftExecutionIdTarget.value : "";
  }

  // Resolved against the live node every time: this link sits in the fragment a draft
  // broadcast replaces, so a reference captured before the await can be detached by then.
  togglePreview(disabled) {
    if (this.hasPreviewTarget) this.previewTarget.classList.toggle("disabled", disabled);
  }

  previewFailed() {
    const slot = document.getElementById(this.previewTargetIdValue);
    if (slot) slot.innerHTML = `<p class="error-message">${this.previewErrorValue}</p>`;
  }

  // Manual edits are steps too, so undo walks back through them as well as drafts.
  remember() {
    const current = this.templateTarget.value;
    if (current && current !== this.history[this.history.length - 1]) this.history.push(current);
  }

  async request() {
    this.remember();
    this.showSpinner();
    this.drafting = true;
    this.requestedFrom = this.criteria();

    const response = await post(this.urlValue, {
      body: {
        agent_name: this.agentName(),
        place: this.agentPlace(),
        condition_name: this.conditionName(),
        criteria: this.requestedFrom,
        phase: this.phaseValue,
        // The saved rung, when there is one, so drafting reads the compiled geography
        // instead of paying for a second reading that can disagree with it.
        action_id: this.actionIdValue,
        siblings: this.siblings(),
        target: this.targetIdValue,
      },
      responseKind: "turbo-stream",
    });

    // Without the slot the response should have rendered, nothing will ever announce a draft.
    if (!response.ok) this.giveUp();
  }

  // Whatever went wrong, leave a box to type in and a button to press again.
  giveUp() {
    this.drafting = false;
    this.reveal();
    this.refreshRegenerate();
  }

  // Keyed on which draft produced it, not on the text.
  adoptDraft() {
    if (!this.hasResultTarget) return false;

    const drafted = this.resultTarget.dataset.draftedText;
    if (!drafted || this.resultTarget.id === this.adoptedFrom) return false;

    this.adoptedFrom = this.resultTarget.id;
    // Which draft this template came from, for the save to adopt its tool selection from.
    // Copied into a server-rendered field: the broadcast fragment has no form builder, so
    // it cannot name itself inside the condition's nested attributes.
    if (this.hasDraftExecutionIdTarget)
      this.draftExecutionIdTarget.value = this.resultTarget.dataset.executionId || "";
    // Recorded before the write, so the input it dispatches settles Regenerate to off.
    this.drafting = false;
    this.drafted = drafted;
    this.draftedFrom = this.requestedFrom;
    this.write(drafted);
    this.reveal();
    return true;
  }

  write(text, { remember = true } = {}) {
    if (remember) this.remember();
    this.templateTarget.value = text;
    // Anything watching the field -- character counters, dirty tracking -- has to see it.
    this.templateTarget.dispatchEvent(new Event("input", { bubbles: true }));
    this.templateTarget.dispatchEvent(new Event("change", { bubbles: true }));
  }

  revealIfWritten() {
    if (this.templateTarget.value.trim()) this.reveal();
  }

  reveal() {
    if (this.hasFieldTarget) this.fieldTarget.classList.remove("no-show");
  }

  // Hide the box rather than show an empty one, and stop a second click landing.
  showSpinner() {
    if (this.hasFieldTarget) this.fieldTarget.classList.add("no-show");
    if (this.hasRegenerateTarget) this.regenerateTarget.classList.add("disabled");
  }

  // The condition's three actions share one form, saved or not.
  siblings() {
    const form = this.element.closest("form");
    if (!form) return {};

    return [...form.querySelectorAll("[data-message-draft-phase]")].reduce((found, field) => {
      const phase = field.dataset.messageDraftPhase;
      if (phase !== this.phaseValue && field.value.trim()) found[phase] = field.value;
      return found;
    }, {});
  }

  // The typed value wins: on the new-agent form nothing is saved yet.
  agentName() {
    return this.fieldValue("craig_agent_name") || this.agentNameValue;
  }

  // A rung usually names no place, and a placeless discovery grounds the draft in
  // whichever country's adapter the registry lists first. Named agentPlace rather than
  // place because `scope`-style bare names collide with Stimulus's own.
  agentPlace() {
    return this.fieldValue("craig_agent_place_name") || this.agentPlaceValue;
  }

  conditionName() {
    return this.nearest("[data-message-draft-condition-name]");
  }

  criteria() {
    return this.nearest("[data-message-draft-criteria]");
  }

  nearest(selector) {
    return this.element.closest("form")?.querySelector(selector)?.value || "";
  }

  fieldValue(id) {
    return document.getElementById(id)?.value || "";
  }
}
